import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";
import { ELECTRICITY_DRIVEN_TECHS, ELECTRICITY_PRESETS, METHODS, METHOD_LABEL, ROUTES, type MethodId } from "@h2lca/core";
import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button, ChoiceButton } from "@/components/ui/FormControls";
import { MethodBadge } from "@/components/workshop/Badges";
import { technologiesFor, useMaster } from "@/store/master";
import { STEPS, stepIndex, useWorkshop } from "@/store/workshop";

const METHOD_OPTIONS: readonly MethodId[] = METHODS;

/**
 * 산정 시작 , 사용자가 먼저 결과의 대상을 정하고, 그 선택으로 기본 설비 구성을 만든다.
 * 기존 예시,빈 시나리오 흐름은 /app/new 에 남겨 두고, 기본 진입점은 이 경로 선택 흐름으로 통일한다.
 */
export function StartPage() {
  const nav = useNavigate();
  const data = useMaster((s) => s.data);
  const scenario = useWorkshop((s) => s.scenario);
  const dirty = useWorkshop((s) => s.dirty);
  const step = useWorkshop((s) => s.step);
  const newFromPathway = useWorkshop((s) => s.newFromPathway);
  const loadExample = useWorkshop((s) => s.loadExample);
  const restoreDraft = useWorkshop((s) => s.restoreDraft);
  const techs = useMemo(() => technologiesFor(data, "production"), [data]);
  const [techId, setTechId] = useState(() => techs.find((t) => t.status === "ready")?.id ?? "");
  const [routeId, setRouteId] = useState(ROUTES[0]!.id);
  const [electricityPresetId, setElectricityPresetId] = useState(ELECTRICITY_PRESETS[0]!.id);
  const [method, setMethod] = useState<MethodId>("general");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!useWorkshop.getState().scenario) restoreDraft();
  }, [restoreDraft]);

  useEffect(() => {
    if (!techs.some((tech) => tech.id === techId && tech.status === "ready")) {
      setTechId(techs.find((tech) => tech.status === "ready")?.id ?? "");
    }
  }, [techId, techs]);

  const selectedTech = techs.find((tech) => tech.id === techId);
  const selectedTechAvailable = selectedTech?.status === "ready";
  const selectedRoute = ROUTES.find((route) => route.id === routeId) ?? ROUTES[0];
  const usesElectricity = ELECTRICITY_DRIVEN_TECHS.has(techId);
  const selectedElectricity = ELECTRICITY_PRESETS.find((preset) => preset.id === electricityPresetId) ?? ELECTRICITY_PRESETS[0];
  const selectedExample = useMemo(
    () => data.examples.find((example) => example.active && example.scenario.basis.productionTechId === techId),
    [data.examples, techId],
  );

  const guard = () => !(scenario && dirty) || window.confirm("작업 중인 시나리오가 있습니다. 저장하지 않은 내용은 사라집니다. 새 산정을 시작할까요?");
  const start = () => {
    if (!selectedTech || !selectedTechAvailable || !selectedRoute || !guard()) return;
    setError(null);
    try {
      newFromPathway({
        techId,
        routeId: selectedRoute.id,
        electricityPresetId: usesElectricity ? electricityPresetId : ELECTRICITY_PRESETS[0]!.id,
        method,
      });
      nav("/app/work");
    } catch {
      setError("기본 설비 구성을 만들지 못했습니다. 참고 데이터가 준비되어 있는지 확인해 주세요.");
    }
  };

  const startExample = () => {
    if (!selectedExample || !selectedTechAvailable || !guard()) return;
    loadExample(selectedExample);
    nav("/app/work");
  };

  return (
    <AppShell>
      <div className="page">
        <PageHeader title="산정 시작" description="대상 수소의 생산방식과 공급경로를 정하면, 기본 설비 구성이 자동으로 만들어집니다." />
        {error && <p className="note note-danger text-footnote" role="alert">{error}</p>}

        <div className="start-layout">
          <div className="start-main">
            <section className="ws-section" aria-labelledby="start-tech-title">
              <div className="ws-section-head">
                <div>
                  <h2 id="start-tech-title" className="ws-section-title">생산방식</h2>
                  <p className="ws-section-desc">분석할 수소가 어떤 공정에서 생산되는지 선택하세요.</p>
                </div>
                <span className="text-footnote text-ink-subtle">필수 선택</span>
              </div>
              <div className="grid grid-cols-1 gap-item sm:grid-cols-2">
                {techs.map((tech) => (
                  <button
                    key={tech.id}
                    type="button"
                    className="tech-choice"
                    aria-pressed={techId === tech.id}
                    disabled={tech.status !== "ready"}
                    onClick={() => setTechId(tech.id)}
                  >
                    <span className="flex items-center justify-between gap-item">
                      <span className="font-semibold text-ink">{tech.name}</span>
                      {tech.status !== "ready" && <span className="badge-soft">검토 후 제공</span>}
                    </span>
                    <span className="text-footnote text-ink-subtle">{tech.description}</span>
                  </button>
                ))}
              </div>
            </section>

            <section className="ws-section" aria-labelledby="start-route-title">
              <div className="ws-section-head">
                <div>
                <h2 id="start-route-title" className="ws-section-title">저장 및 이송 경로</h2>
                  <p className="ws-section-desc">어느 지점까지를 이번 산정에 포함할지 정합니다.</p>
                </div>
                <span className="text-footnote text-ink-subtle">필수 선택</span>
              </div>
              <div className="grid grid-cols-1 gap-item lg:grid-cols-2">
                {ROUTES.map((route) => (
                  <ChoiceButton key={route.id} selected={route.id === routeId} className="start-choice" onClick={() => setRouteId(route.id)}>
                    <span className="flex items-center justify-between gap-item">
                      <span className="font-semibold text-ink">{route.name}</span>
                      {route.id === routeId && <span className="start-choice-mark">선택됨</span>}
                    </span>
                    <span className="text-footnote leading-relaxed text-ink-subtle">{route.description}</span>
                    <span className="mt-label text-micro text-ink-muted">인도 지점: {route.fu.deliveryPoint}</span>
                  </ChoiceButton>
                ))}
              </div>
            </section>

            <section className="ws-section" aria-labelledby="start-electricity-title">
              <div className="ws-section-head">
                <div>
                  <h2 id="start-electricity-title" className="ws-section-title">전력원</h2>
                  <p className="ws-section-desc">전력을 사용하는 생산방식이면 기본 전력 믹스를 선택할 수 있습니다. 세부 비율은 ‘운전자료 입력’에서 조정합니다.</p>
                </div>
              </div>
              {usesElectricity ? (
                <div className="grid grid-cols-1 gap-item sm:grid-cols-3">
                  {ELECTRICITY_PRESETS.map((preset) => (
                    <ChoiceButton key={preset.id} selected={preset.id === electricityPresetId} className="start-choice" onClick={() => setElectricityPresetId(preset.id)}>
                      <span className="font-semibold text-ink">{preset.label}</span>
                      <span className="text-footnote text-ink-subtle">{preset.description}</span>
                    </ChoiceButton>
                  ))}
                </div>
              ) : (
                <p className="note note-info text-footnote">현재 생산방식에서는 전력원 선택이 기본 경로 구성에 직접 적용되지 않습니다. 산정 화면에서 필요한 자료를 확인할 수 있습니다.</p>
              )}
            </section>

            <section className="ws-section" aria-labelledby="start-method-title">
              <div className="ws-section-head">
                <div>
                  <h2 id="start-method-title" className="ws-section-title">산정 방식</h2>
                  <p className="ws-section-desc">일반 LCA 또는 청정수소 인증제 모의산정 기준으로 시작합니다.</p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-item sm:grid-cols-2">
                {METHOD_OPTIONS.map((option) => {
                  const config = data.methods.find((item) => item.id === option);
                  return (
                    <ChoiceButton key={option} selected={option === method} className="start-choice" onClick={() => setMethod(option)}>
                      <span className="flex items-center justify-between gap-item">
                        <span className="font-semibold text-ink">{METHOD_LABEL[option]}</span>
                        {option === method && <span className="start-choice-mark">선택됨</span>}
                      </span>
                      <span className="text-footnote leading-relaxed text-ink-subtle">{config?.description ?? "산정 기준을 선택합니다."}</span>
                    </ChoiceButton>
                  );
                })}
              </div>
            </section>
          </div>

          <aside className="start-aside" aria-label="산정 시작 요약">
            <section className="start-summary">
              <div className="flex items-start justify-between gap-content">
                <div>
                  <p className="text-caption font-semibold text-ink-subtle">새 산정</p>
                  <h2 className="mt-label text-head font-bold text-ink">선택 내용 확인</h2>
                </div>
                <MethodBadge method={method} />
              </div>
              <dl className="start-summary-list">
                <div><dt>생산방식</dt><dd>{selectedTech?.name ?? "선택 필요"}</dd></div>
                <div><dt>저장 및 이송</dt><dd>{selectedRoute?.name ?? "선택 필요"}</dd></div>
                <div><dt>전력원</dt><dd>{usesElectricity ? selectedElectricity?.label : "생산방식에 따라 적용 안 함"}</dd></div>
                <div><dt>인도 지점</dt><dd>{selectedRoute?.fu.deliveryPoint ?? "없음"}</dd></div>
              </dl>
              <Button variant="primary" fullWidth disabled={!selectedTechAvailable || !selectedRoute} onClick={start}>
                이 조건으로 산정 시작
              </Button>
              {selectedExample && selectedTechAvailable && (
                <Button variant="soft" fullWidth onClick={startExample}>
                  {selectedTech?.name} 예시로 시작
                </Button>
              )}
              <Link to="/app/scenarios" className="start-secondary-link">내 시나리오에서 이어서 하기</Link>
            </section>

            {scenario && (
              <section className="start-resume">
                <p className="text-caption font-semibold text-ink-subtle">현재 작업</p>
                <p className="mt-label text-body font-bold text-ink">{scenario.name}</p>
                <p className="mt-label text-footnote text-ink-subtle">{stepIndex(step)} / {STEPS.length} 단계, {STEPS.find((item) => item.id === step)?.label}</p>
                <Button variant="secondary" fullWidth onClick={() => nav("/app/work")}>현재 작업 열기</Button>
              </section>
            )}

            <p className="text-footnote leading-relaxed text-ink-subtle">처음이라면 예시로 시작한 뒤 입력값과 설비 구성을 실제 조건에 맞게 바꿀 수 있습니다. 임시 기본값은 결과에서 출처와 상태를 확인할 수 있습니다.</p>
          </aside>
        </div>
      </div>
    </AppShell>
  );
}
